import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Gauge,
  Image as ImageIcon,
  Loader2,
  MousePointerClick,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Timer,
  Zap,
} from "lucide-react";
import { usePagePerformance } from "@/hooks/use-page-builder-ux";
import { usePageList, type PageSummary } from "@/hooks/use-pages";
import { usePageRecommendations } from "@/hooks/use-self-learning";

function formatMs(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return "No audit";
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}s`;
  }
  return `${Math.round(value)}ms`;
}

function formatScore(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return "No score";
  }
  return `${Math.round(value)}`;
}

function formatCls(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return "No audit";
  }
  return value.toFixed(3);
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "No Lighthouse audit yet";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Audit timestamp unavailable";
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function scoreTone(score: number | null | undefined): string {
  if (score == null) {
    return "text-muted-foreground";
  }
  if (score >= 90) {
    return "text-emerald-600";
  }
  if (score >= 50) {
    return "text-amber-600";
  }
  return "text-red-600";
}

function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
}: {
  label: string;
  value: string;
  detail: string;
  icon: typeof Gauge;
}) {
  return (
    <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <p className="mt-3 text-2xl font-bold tabular-nums text-foreground">{value}</p>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">{detail}</p>
    </article>
  );
}

function pageLabel(page: PageSummary): string {
  return `${page.title} /${page.slug}`;
}

export default function PagesPerformancePage() {
  const { pages, isLoading: isLoadingPages, error: pageError } = usePageList();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const selectablePages = useMemo(
    () => pages.filter((page) => page.status === "published" || page.status === "draft"),
    [pages],
  );
  const effectivePageId = selectedPageId ?? selectablePages[0]?.id ?? null;
  const selectedPage = selectablePages.find((page) => page.id === effectivePageId) ?? null;

  const {
    performance,
    isLoading: isLoadingPerformance,
    error: performanceError,
  } = usePagePerformance(effectivePageId ?? undefined);
  const {
    recommendations,
    isLoading: isLoadingRecommendations,
    error: recommendationsError,
    apply,
    dismiss,
  } = usePageRecommendations(effectivePageId ?? undefined, { surface: "dashboard" });
  const performanceRecommendations = useMemo(
    () => recommendations.filter((recommendation) => recommendation.category === "performance"),
    [recommendations],
  );

  const hasAnyMetric = Boolean(performance?.auditedAt || performance?.performanceScore != null);

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[32px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.18),_transparent_36%),linear-gradient(135deg,rgba(15,23,42,0.05),rgba(15,23,42,0))] p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-muted-foreground">Pages Performance</p>
            <h1 className="max-w-3xl text-3xl font-bold tracking-tight text-foreground">
              Lighthouse-backed performance, wired to the mounted Pages contract.
            </h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              Pick a real page, review the latest queued publish audit, and apply dashboard recommendations when the optimization contract exposes an action.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/pages/sites"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
            >
              Page inventory
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={selectedPage ? `/pages/editor/${selectedPage.id}` : "/pages/new"}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
            >
              Open editor
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <article className="rounded-[26px] border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground">Select page</h2>
              <p className="text-sm leading-6 text-muted-foreground">
                Options come from <code className="rounded bg-muted px-1.5 py-0.5">usePageList()</code>; no placeholder inventory is used.
              </p>
            </div>
            {isLoadingPages && (
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading pages
              </span>
            )}
          </div>

          {pageError && (
            <p className="mt-4 rounded-xl border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
              {pageError}
            </p>
          )}

          <select
            value={effectivePageId ?? ""}
            onChange={(event) => setSelectedPageId(event.target.value || null)}
            className="mt-5 w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm font-medium text-foreground outline-none transition focus:border-primary"
            aria-label="Select page for performance audit"
          >
            {selectablePages.length === 0 ? (
              <option value="">No draft or published pages available</option>
            ) : (
              selectablePages.map((page) => (
                <option key={page.id} value={page.id}>
                  {pageLabel(page)}
                </option>
              ))
            )}
          </select>
        </article>

        <article className="rounded-[26px] border border-border bg-card p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Performance score</p>
              <p className={`mt-2 text-5xl font-black tabular-nums ${scoreTone(performance?.performanceScore)}`}>
                {isLoadingPerformance ? "..." : formatScore(performance?.performanceScore)}
              </p>
            </div>
            <Gauge className="h-7 w-7 text-primary" />
          </div>
          <p className="mt-4 text-sm leading-6 text-muted-foreground">
            Audit freshness: <span className="font-medium text-foreground">{formatDate(performance?.auditedAt)}</span>.
            Lighthouse audits are queued by publish; this page does not trigger manual audits.
          </p>
        </article>
      </section>

      {performanceError && (
        <p className="rounded-xl border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          {performanceError}
        </p>
      )}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard label="LCP" value={formatMs(performance?.lcpMs)} detail="Largest Contentful Paint from the latest Lighthouse audit." icon={Timer} />
        <MetricCard label="CLS" value={formatCls(performance?.cls)} detail="Cumulative Layout Shift from lab audit data." icon={RefreshCw} />
        <MetricCard label="TBT, not field INP" value={formatMs(performance?.tbtMs ?? performance?.inpMs)} detail="Current contract exposes a TBT alias over the legacy inpMs storage column until real field INP is proven." icon={MousePointerClick} />
        <MetricCard label="TTFB" value={formatMs(performance?.ttfbMs)} detail="Server response timing when Lighthouse provides it." icon={Clock3} />
        <MetricCard label="FCP" value={formatMs(performance?.fcpMs)} detail="First Contentful Paint when available from the audit." icon={Zap} />
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <ImageIcon className="h-5 w-5 text-primary" />
          <h3 className="mt-4 text-base font-semibold text-foreground">Published renderer boundary</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Eligible published media are rewritten through <code className="rounded bg-muted px-1.5 py-0.5">img.stero.ai</code>.
            The first content image is eager/high priority; following content images are lazy/low priority.
          </p>
        </article>
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <ShieldAlert className="h-5 w-5 text-primary" />
          <h3 className="mt-4 text-base font-semibold text-foreground">Field data honesty</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Field INP is not proven on this route while the mounted hook only returns lab timing. The UI labels that metric as TBT, not field INP.
          </p>
        </article>
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          <h3 className="mt-4 text-base font-semibold text-foreground">Audit source</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Scores and Core Web Vitals come from <code className="rounded bg-muted px-1.5 py-0.5">usePagePerformance(pageId)</code>.
            Dashboard suggestions come from <code className="rounded bg-muted px-1.5 py-0.5">usePageRecommendations(pageId, {"{ surface: \"dashboard\" }"})</code>.
          </p>
        </article>
      </section>

      <section className="rounded-[26px] border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Performance recommendations</h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Action buttons appear only when the mounted recommendation includes an executable action.
            </p>
          </div>
          {isLoadingRecommendations && (
            <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading recommendations
            </span>
          )}
        </div>

        {recommendationsError && (
          <p className="mt-4 rounded-xl border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
            {recommendationsError}
          </p>
        )}

        <div className="mt-5 grid gap-3">
          {performanceRecommendations.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border p-5 text-sm leading-6 text-muted-foreground">
              {hasAnyMetric
                ? "No performance recommendations are currently open for this page."
                : "Publish this page to queue a Lighthouse audit, then return here for measured recommendations."}
            </p>
          ) : (
            performanceRecommendations.map((recommendation) => (
              <article key={recommendation.id} className="rounded-2xl border border-border bg-background p-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold text-foreground">{recommendation.title}</h3>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground">
                        {recommendation.priority}
                      </span>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground">
                        {recommendation.category}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">{recommendation.message}</p>
                    {recommendation.signalNotes.length > 0 && (
                      <p className="mt-2 text-xs leading-5 text-muted-foreground">{recommendation.signalNotes[0]}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {recommendation.action && (
                      <button
                        type="button"
                        onClick={() => void apply(recommendation.id)}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
                      >
                        {recommendation.action.label}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void dismiss(recommendation.id)}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
